import { z } from 'zod';
// =============================================================================
// Campaign attribution
// =============================================================================
export const CAMPAIGN_ATTRIBUTION_VALUE_MAX_LENGTH = 200;
// Values arrive from ad and landing-page URLs, so they are user-controlled:
// bounded, and click ids restricted to the URL-safe alphabet Google issues.
const campaignValueSchema = z.string().trim().min(1).max(CAMPAIGN_ATTRIBUTION_VALUE_MAX_LENGTH).regex(/^[^\p{Cc}]+$/u);
const clickIdSchema = z.string().trim().min(1).max(CAMPAIGN_ATTRIBUTION_VALUE_MAX_LENGTH).regex(/^[A-Za-z0-9_-]+$/);
/**
 * The landing site's signup buttons, by the `data-cta` id each carries and stamps on its
 * link as `?cta=`. A closed list so the id can sit in the anonymous UI usage events and in
 * registration attribution without any free text reaching the events table.
 *
 * Removing an id costs only the anonymous events a stale landing bundle still sends for it
 * (refused and dropped); registration stays unaffected, because `CampaignAttributionSchema`
 * drops an unregistered `cta` rather than refusing the request.
 */
export const LANDING_SIGNUP_CTAS = [
    'nav-trial',
    'mobile-trial',
    'hero-trial',
    'trial-google',
    'trial-microsoft',
    'trial-email',
    'advocates-hero-trial',
    'attorneys-hero-trial',
    'law-firms-hero-trial',
];
export const LandingSignupCtaSchema = z.enum(LANDING_SIGNUP_CTAS);
// Accepts any slug on the wire and yields a registered id or undefined. A closed enum here
// would make the route's AJV refuse a whole registration (400) for an id a stale bundle still
// sends after it was removed, and `.catch` would not help: the input JSON schema keeps the enum.
const ctaSchema = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9-]+$/)
    .transform((value) => LandingSignupCtaSchema.safeParse(value).data);
/**
 * Campaign attribution for leads and signups: the UTM parameters, the
 * Google Ads click ids, the landing page's `?for=` audience and the `?cta=`
 * signup button it was reached from. Recorded on events only, never stored on
 * a row. Unknown keys are stripped, so the object is an allowlist.
 */
export const CampaignAttributionSchema = z.object({
    utmSource: campaignValueSchema.optional(),
    utmMedium: campaignValueSchema.optional(),
    utmCampaign: campaignValueSchema.optional(),
    utmTerm: campaignValueSchema.optional(),
    utmContent: campaignValueSchema.optional(),
    gclid: clickIdSchema.optional(),
    gbraid: clickIdSchema.optional(),
    wbraid: clickIdSchema.optional(),
    audience: z.string().trim().min(1).max(64).regex(/^[A-Za-z0-9_-]+$/).optional(),
    cta: ctaSchema.optional(),
});
/** The URL query parameter each attribution field travels in. */
export const CAMPAIGN_ATTRIBUTION_URL_PARAMS = {
    utmSource: 'utm_source',
    utmMedium: 'utm_medium',
    utmCampaign: 'utm_campaign',
    utmTerm: 'utm_term',
    utmContent: 'utm_content',
    gclid: 'gclid',
    gbraid: 'gbraid',
    wbraid: 'wbraid',
    audience: 'for',
    cta: 'cta',
};
/**
 * Reads the allowlisted campaign parameters from a query string. A value that
 * fails validation is dropped on its own rather than discarding the rest, so one
 * malformed parameter never costs the signup its campaign.
 */
export function parseCampaignAttribution(search) {
    const params = new URLSearchParams(search);
    const attribution = {};
    for (const [field, param] of Object.entries(CAMPAIGN_ATTRIBUTION_URL_PARAMS)) {
        const parsed = CampaignAttributionSchema.shape[field].safeParse(params.get(param) ?? undefined);
        if (parsed.success && parsed.data !== undefined) {
            Object.assign(attribution, { [field]: parsed.data });
        }
    }
    return Object.keys(attribution).length > 0 ? attribution : null;
}
